import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common";
import { MaterialPicker } from "./MaterialPicker";
import { useLfUom, lfText, lfPrice, perLf } from "@/lib/lfUom";

export const jobTypeOf = (cfg, l) => (cfg.job_types?.[l.service_line] || []).find((j) => j.code === l.job_type);
export const jobKeys = (cfg) => Object.keys(cfg.job_fields || {});

const SHEETS = { SRA3: [320, 450], SRA2: [450, 640], SRA1: [640, 900], A4: [210, 297], A3: [297, 420], A2: [420, 594], A1: [594, 841], B3: [353, 500], B2: [500, 707], B1: [707, 1000] };
const MM = { mm: 1, cm: 10, in: 25.4, ft: 304.8, m: 1000 };
export const isSheet = (m) => !!m && ["sheet", "ream", "pack", "box"].includes(m.unit) && m.form !== "roll";
const subDims = (m) => (Number(m.width) && Number(m.length) ? [m.width * (MM[m.dim_unit] || 1), m.length * (MM[m.dim_unit] || 1)] : sheetDims(m.size));
const sheetDims = (size) => {
  const s = String(size || "").toUpperCase();
  const m = s.match(/(\d{2,4})\s*[X×]\s*(\d{2,4})/);
  if (m) return [Number(m[1]), Number(m[2])];
  const k = Object.keys(SHEETS).find((x) => new RegExp(`\\b${x}\\b`).test(s));
  return k ? SHEETS[k] : null;
};
export function calcUps(cfg, l, sub) {
  const dims = isSheet(sub) && subDims(sub);
  const carton = l.job_type === "folding_cartons" && l.flat_width_mm && l.flat_length_mm;
  const w = Number(carton ? l.flat_width_mm : l.width_mm), h = Number(carton ? l.flat_length_mm : l.length_mm);
  if (!dims || !w || !h) return null;
  const b = Number(cfg.job_rates?.imposition_bleed_mm || 0), g = Number(cfg.job_rates?.imposition_gutter_mm || 0);
  const fit = (pw, ph) => Math.floor((dims[0] + g) / (pw + 2 * b + g)) * Math.floor((dims[1] + g) / (ph + 2 * b + g));
  return Math.max(fit(w, h), fit(h, w));
}
export function applyUps(cfg, l) {
  if (l.ups_manual) return l;
  const sub = cfg.materials.find((m) => m.code === l.substrate);
  const jt = jobTypeOf(cfg, l);
  if (jt?.fields.includes("labels_per_row")) return applyLabelLayout(cfg, l, sub);
  const n = calcUps(cfg, l, sub);
  return n === null ? l : { ...l, ups: n > 0 ? n : "" };
}
function applyLabelLayout(cfg, l, sub) {
  const w = Number(l.width_mm), h = Number(l.length_mm);
  if (!sub || !w || !h) return l;
  const p = 2 * Number(l.bleed_mm || 0) + Number(l.gap_between_labels_mm || 0);
  if (isSheet(sub)) {
    const dims = subDims(sub);
    if (!dims) return l;
    return { ...l, labels_per_row: Math.floor(dims[0] / (w + p)) || "", labels_per_column: Math.floor(dims[1] / (h + p)) || "" };
  }
  const web = Number(String(sub.size || "").match(/(\d{2,5})\s*mm/)?.[1] || 0);
  return web ? { ...l, labels_per_row: Math.floor(web / (w + p)) || "" } : l;
}
export const LAYOUT_KEYS = ["ups", "labels_per_row", "labels_per_column"];

export const CALENDAR_CODES = ["wall_calendar", "tent_calendar", "flip_calendar", "poster_calendar", "diary_planner"];

/** Product picker: general job types, plus Product = Calendar which reveals a Calendar type dropdown fed by the Calendar Types library. */
export function ProductTypePicker({ cfg, types, value, onPick, testId, label = "Job type", placeholder = "Select job type" }) {
  const isCal = CALENDAR_CODES.includes(value);
  const [calMode, setCalMode] = useState(isCal);
  useEffect(() => { if (isCal) { setCalMode(true); } }, [isCal]);
  const avail = new Set(types.map((j) => j.code));
  const cals = (cfg?.calendar_types || []).filter((r) => avail.has(r.code));
  const opts = [...types.filter((j) => !CALENDAR_CODES.includes(j.code)).map((j) => ({ value: j.code, label: j.label })), ...(cals.length ? [{ value: "__calendar", label: "Calendar" }] : [])];
  return (
    <>
      <Field label={label}><NativeSelect testId={testId} value={calMode ? "__calendar" : value || ""} placeholder={placeholder} options={opts}
        onChange={(v) => { if (v === "__calendar") { setCalMode(true); return; } setCalMode(false); onPick(v); }} /></Field>
      {calMode && <Field label="Calendar type *"><NativeSelect testId={`${testId}-calendar-type`} value={isCal ? value : ""} onChange={onPick} placeholder="Select calendar type"
        options={cals.map((r) => ({ value: r.code, label: r.name || types.find((j) => j.code === r.code)?.label }))} /></Field>}
    </>
  );
}

export function JobTypeSelect({ cfg, l, onPick, testId }) {
  return <ProductTypePicker key={l.service_line} cfg={cfg} types={cfg.job_types?.[l.service_line] || []} value={l.job_type} onPick={onPick} testId={testId} />;
}

export function JobField({ k, def: raw, l, set, subs, tid }) {
  const u = useLfUom();
  const lf = l.service_line === "large_format";
  const def = lf ? { ...raw, label: lfText(raw.label, u), options: raw.options?.map((o) => ({ ...o, label: lfText(o.label, u) })) } : raw;
  const v = l[k];
  if (def.type === "bool") return (
    <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-700"><input type="checkbox" data-testid={tid(k)} className="h-4 w-4 accent-[#13294B]" checked={!!v} onChange={(e) => set(k, e.target.checked)} />{def.label}</label>);
  if (def.type === "number") return <Field label={def.label}><Input type="number" data-testid={tid(k)} value={v ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} /></Field>;
  if (def.type === "text" || def.type === "date") return <Field label={def.label}><Input type={def.type} data-testid={tid(k)} value={v ?? ""} onChange={(e) => set(k, e.target.value)} /></Field>;
  if (def.type === "substrate") return <Field label={def.label}><MaterialPicker testId={tid(k)} mats={subs} value={v} onChange={(x) => set(k, x)} {...(lf ? { priceOf: (m) => lfPrice(m, u) } : {})} /></Field>;
  const options = def.type === "variant" ? def.variants.map((x) => ({ value: x.name, label: `${x.name}${x.width_mm ? ` · ${x.width_mm}mm` : ""} · ${lf ? `${perLf(x.cost_per_m2, u)}/${u}` : `${x.cost_per_m2}/m2`}` }))
    : def.type === "gsm" ? def.gsmOptions.map((g) => ({ value: String(g), label: `${g} gsm` })) : def.options;
  return <Field label={def.label}><NativeSelect testId={tid(k)} value={v == null ? "" : String(v)} onChange={(x) => set(k, def.type === "gsm" ? Number(x) : x)} placeholder="Select" options={options} /></Field>;
}

export function JobFields({ cfg, l, set, subs, sub, tid }) {
  const jt = jobTypeOf(cfg, l);
  if (!jt) return null;
  const gsmOptions = (sub?.gsm_options?.length ? sub.gsm_options : sub?.gsm ? [sub.gsm] : []);
  const variants = sub && !Number(sub.gsm) ? (cfg.substrate_variants || []).filter((x) => x.material_code === sub.code) : [];
  const visible = jt.fields.filter((k) => k !== "quantity_unit" && cfg.job_fields[k]).filter((k) => {
    const s = cfg.job_fields[k].show_if;
    if (!s) return true;
    if (s.includes("=")) { const [key, val] = s.split("="); return String(l[key] ?? "") === val; }
    return !(jt.fields.includes(s) && !l[s]);
  })
    .filter((k) => cfg.job_fields[k].type !== "gsm" || gsmOptions.length).filter((k) => cfg.job_fields[k].type !== "variant" || variants.length);
  return visible.map((k) => <JobField key={k} k={k} def={{ ...cfg.job_fields[k], gsmOptions, variants }} l={l} set={set} subs={subs} tid={tid} />);
}
